import React from "react";
import { apiUploadPdf } from "./apiClient";
import { getApiBaseUrl } from "../config/appSettings";

export function PdfUploadField({
  label,
  value,
  onChange,
  labelClassName,
  companyName = "",
  replacePath = ""
}) {
  const labelCls =
    labelClassName ?? "mb-1 block text-sm font-medium text-slate-700";
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Allow selecting the same file again after a failed/replaced upload.
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    const res = await apiUploadPdf("/api/Uploads/pdf", file, {
      companyName: companyName || "",
      replacePath: replacePath || value || ""
    });
    if (!res.ok) {
      setError(res.error ?? "Upload failed.");
    } else {
      const uploadedPath = String(res.data?.path ?? res.data?.Path ?? "").trim();
      if (!uploadedPath) {
        setError("Upload succeeded but no PDF path was returned.");
      } else {
        onChange(uploadedPath);
      }
    }
    setUploading(false);
  };

  const canPreview = typeof value === "string" && value.trim().length > 0;
  const baseUrl = getApiBaseUrl();
  const previewHref = (() => {
    if (!canPreview) return "";
    const raw = value.trim();
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) {
      // Rewrite static /uploads/*.pdf URLs to the API file endpoint (more reliable on IIS).
      try {
        const url = new URL(raw);
        const match = url.pathname.match(/\/uploads\/([^/]+\.pdf)$/i);
        if (match) {
          return `${baseUrl}/api/Uploads/file/${encodeURIComponent(match[1])}`;
        }
      } catch {
        /* keep original */
      }
      return raw;
    }

    const fileName = raw.split("/").pop();
    if (fileName && /\.pdf$/i.test(fileName)) {
      return `${baseUrl}/api/Uploads/file/${encodeURIComponent(fileName)}`;
    }

    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${baseUrl}${path}`;
  })();

  return (
    <label className="block min-w-0">
      <span className={labelCls}>{label}</span>
      <input
        type="file"
        accept="application/pdf,.pdf"
        onChange={onFileChange}
        className="block w-full text-sm text-slate-600"
      />
      {uploading && <span className="text-xs text-slate-500">Uploading…</span>}
      {error && <span className="text-xs text-red-600 block">{error}</span>}
      {canPreview && (
        <div className="mt-2 flex items-center gap-3">
          <a
            href={previewHref}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            View PDF
          </a>
          <span className="text-xs text-slate-400 truncate max-w-[240px]" title={value}>
            {String(value).split("/").pop()}
          </span>
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs text-slate-500 hover:text-red-600"
          >
            Remove
          </button>
        </div>
      )}
    </label>
  );
}
